import * as React from 'react';
import { connect } from 'redux-scaffolding-ts';
import { CustomizationDataDto, CustomizationDataStore } from '../../stores/configuration/events-n-requests/customization-data-store';
import { nameof } from '../../utils/object';
import { ItemReference } from '../../stores/dataStore';
import { isNullOrWhiteSpaces } from '../../utils/useful-functions';
import { useCallback, useEffect, useState } from 'react';
import TailwindSelect from 'widgets/editors/tailwind-select';
import { useTranslation } from 'react-i18next';

interface TailwindCustomizationDataEditorProps {
  value: ItemReference;
  onChange: (value: ItemReference) => void;
  className?: string;
  placeholder?: string;
  customizationDatas?: CustomizationDataStore;
  nullable?: boolean;
  minWidth?: number;
  reloadOnChange?: boolean;
  clearable?: boolean;
  readOnly?: boolean;
  loadDataOnOpen?: boolean;
}

const TailwindCustomizationDataEditor: React.FC<TailwindCustomizationDataEditorProps> = ({
  value,
  onChange,
  className = '',
  placeholder,
  customizationDatas,
  nullable = false,
  minWidth,
  reloadOnChange,
  clearable = true,
  readOnly = false,
  loadDataOnOpen = false
}) => {
  const { t } = useTranslation();
  const [selectedValue, setSelectedValue] = useState<ItemReference>(value);
  const [options, setOptions] = useState<{ value: string; label: string }[]>([]);
  const [isLoading, setIsLoading] = useState(false);

  const loadCustomizationData = useCallback(
    async (search: string = '') => {
      try {
        setIsLoading(true);
        const result = await customizationDatas.getAllAsync({
          searchQuery: '',
          skip: 0,
          take: 100000,
          orderBy: [{ direction: 'Ascending', field: nameof<CustomizationDataDto>('name'), useProfile: false }],
          filter: isNullOrWhiteSpaces(search)
            ? []
            : [`startswith(tolower(${nameof<CustomizationDataDto>('name')}), '${search.toLowerCase()}')`]
        });

        setOptions(
          result.items.map(item => ({
            value: item.id,
            label: item.name
          }))
        );
      } catch (error) {
        console.error('Error loading locations:', error);
        setOptions([]);
      } finally {
        setIsLoading(false);
      }
    },
    [customizationDatas]
  );

  const onOpenDropDown = useCallback(() => {
    if (loadDataOnOpen && options.length === 0) {
      loadCustomizationData();
    }
  }, [loadDataOnOpen, options.length, loadCustomizationData]);

  useEffect(() => {
    if (!loadDataOnOpen || (value && options.length === 0)) {
      loadCustomizationData();
    }
  }, [loadDataOnOpen, value, options.length, loadCustomizationData]);

  useEffect(() => {
    setSelectedValue(value);
  }, [value]);

  const handleChange = (newValue: string | null) => {
    const selected = newValue ? options.find(opt => opt.value === newValue) : null;

    const itemRef = selected ? { id: selected.value, title: selected.label } : null;

    setSelectedValue(itemRef);
    onChange?.(itemRef);
  };

  return (
    <TailwindSelect
      options={
        isLoading
          ? [{ id: 'loading', text: t('Loading...'), disabled: true }]
          : options.length > 0
          ? options.map(training => ({
              id: training.value,
              text: training.label
            }))
          : [{ id: 'no-options', text: t('No options available'), disabled: true }]
      }
      value={selectedValue?.id || ''}
      onChange={handleChange}
      placeholder={placeholder || 'Select an option'}
      clearable
      searchable={!isLoading}
      disabled={readOnly}
      className="w-full"
      onOpenDropDown={onOpenDropDown}
    />
  );
};

// Wire up the React component to the Redux store
export default connect(['customizationDatas', CustomizationDataStore])(TailwindCustomizationDataEditor);
